/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type DeveloperClientControls = {
  showInstallGuide: boolean;
  onToggleInstallGuide: () => void;
  onScheduleNotificationTest: (kind: 'push' | 'toast') => void;
};

type Registration = { token: symbol; controls: DeveloperClientControls };
const Context = createContext<{
  registration: Registration | null;
  setRegistration: React.Dispatch<React.SetStateAction<Registration | null>>;
} | null>(null);

export function DeveloperControlsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [registration, setRegistration] = useState<Registration | null>(null);
  return (
    <Context.Provider value={{ registration, setRegistration }}>
      {children}
    </Context.Provider>
  );
}

export function useDeveloperClientControls(controls: DeveloperClientControls) {
  const setRegistration = useContext(Context)?.setRegistration;
  const latestControls = useRef(controls);
  useLayoutEffect(() => {
    latestControls.current = controls;
  });
  const { showInstallGuide } = controls;
  useEffect(() => {
    if (!import.meta.env.DEV || !setRegistration) return;
    const token = Symbol();
    setRegistration({
      token,
      controls: {
        showInstallGuide,
        onToggleInstallGuide: () =>
          latestControls.current.onToggleInstallGuide(),
        onScheduleNotificationTest: (kind) =>
          latestControls.current.onScheduleNotificationTest(kind),
      },
    });
    return () =>
      setRegistration((current) => (current?.token === token ? null : current));
  }, [setRegistration, showInstallGuide]);
}

export function useRegisteredDeveloperControls() {
  return useContext(Context)?.registration?.controls;
}
